import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { queueLead } from "../lib/offlineQueue";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter,
} from "./ui/drawer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";

const empty = {
  first_name: "", last_name: "", email: "", phone: "", business_name: "",
  interest: "", notes: "", consent_accepted: false,
};

export const ManualLeadSheet = ({ open, onClose, onDone, activeEvent, selectableEvents = [] }) => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState(empty);
  const [eventId, setEventId] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.first_name.trim()) return toast.error(t("capture.nameRequired"));
    if (!form.email.trim() && !form.phone.trim()) return toast.error(t("capture.contactRequired"));
    if (!form.consent_accepted) return toast.error(t("capture.consentRequired"));
    setBusy(true);
    const payload = {
      ...form,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      language: i18n.language.slice(0, 2),
      event_id: activeEvent ? activeEvent.event_id : (eventId || null),
    };
    const queueIt = async () => {
      await queueLead({
        ...payload,
        event_title: activeEvent?.event_title
          || selectableEvents.find((e) => e.id === eventId)?.title || null,
        needs_event: !activeEvent && !eventId && selectableEvents.length === 0,
      });
      toast.info(t("qr.offlineQueued"));
      setForm(empty); setEventId("");
      onDone();
    };
    if (!navigator.onLine) {
      await queueIt();
      setBusy(false);
      return;
    }
    try {
      await api.post("/me/leads", payload);
      toast.success(t("qr.manualSaved"));
      setForm(empty); setEventId("");
      onDone();
    } catch (e) {
      if (!e.response) await queueIt();
      else toast.error(/not available for this Ambassador/i.test(apiError(e))
        ? t("capture.eventUnavailable") : apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DrawerContent data-testid="manual-lead-sheet">
        <div className="mx-auto max-h-[85vh] w-full max-w-[520px] overflow-y-auto">
          <DrawerHeader>
            <DrawerTitle className="font-display text-xl font-bold">{t("qr.manualTitle")}</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-3 px-4 pb-2">
            <p className="text-xs text-muted-foreground" data-testid="manual-lead-hint">
              {t("qr.manualHint")}
            </p>
            {!navigator.onLine && (
              <p className="rounded-xl bg-[hsl(var(--warning))]/10 p-3 text-xs font-semibold"
                data-testid="manual-lead-offline-note">
                {t("qr.offlineNote")}
              </p>
            )}
            {activeEvent ? (
              <p className="rounded-xl bg-muted p-3 text-xs font-semibold" data-testid="manual-lead-event">
                {activeEvent.event_title}
                {activeEvent.partner_name ? ` · ${activeEvent.partner_name}` : ""}
              </p>
            ) : selectableEvents.length > 0 && (
              <label className="block text-xs font-bold uppercase text-muted-foreground">
                {t("capture.chooseEvent")}
                <select data-testid="manual-lead-event-select" value={eventId}
                  onChange={(e) => setEventId(e.target.value)}
                  className="mt-1 h-12 w-full rounded-md border border-input bg-background px-3 text-sm font-semibold text-foreground">
                  <option value="">{t("capture.noEvent")}</option>
                  {selectableEvents.map((e) => (
                    <option key={e.id} value={e.id}>{e.title}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              <Input data-testid="manual-lead-first-name" className="h-12" placeholder={t("capture.firstName")}
                value={form.first_name} onChange={set("first_name")} />
              <Input data-testid="manual-lead-last-name" className="h-12" placeholder={t("capture.lastName")}
                value={form.last_name} onChange={set("last_name")} />
            </div>
            <Input data-testid="manual-lead-phone" className="h-12" type="tel" placeholder={t("capture.phone")}
              value={form.phone} onChange={set("phone")} />
            <Input data-testid="manual-lead-email" className="h-12" type="email" placeholder={t("capture.email")}
              value={form.email} onChange={set("email")} />
            <Input data-testid="manual-lead-business" className="h-12" placeholder={t("capture.business")}
              value={form.business_name} onChange={set("business_name")} />
            <Input data-testid="manual-lead-interest" className="h-12" placeholder={t("capture.interest")}
              value={form.interest} onChange={set("interest")} />
            <Textarea data-testid="manual-lead-notes" rows={3} placeholder={t("capture.notes")}
              value={form.notes} onChange={set("notes")} />
            <label className="flex items-start gap-3 text-sm">
              <Checkbox data-testid="manual-lead-consent" checked={form.consent_accepted}
                onCheckedChange={(v) => setForm({ ...form, consent_accepted: !!v })} />
              <span className="text-muted-foreground">{t("qr.manualConsent")}</span>
            </label>
          </div>
          <DrawerFooter>
            <Button data-testid="manual-lead-submit" onClick={submit} disabled={busy}
              className="h-14 w-full rounded-full text-base font-bold">
              {busy ? t("common.sending") : t("qr.manualSubmit")}
            </Button>
            <Button data-testid="manual-lead-cancel" variant="outline" onClick={onClose}
              className="h-12 w-full rounded-full font-semibold">{t("common.cancel")}</Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
